import {
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFilter,
} from 'api/dashboard/widget';
import { MemberType, Role, Team } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { EffortPlanningConfig } from 'components/organisms/effort-planning-modal/types';
import { TemplatesAPI } from 'data/templates/api';
import type {
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
  ProjectTemplateTask,
  TemplateAllocation,
} from 'data/templates/projects';
import type { ProjectTemplateOption } from 'queries/templates/project-templates';
import { logger } from 'services';
import { ProjectData, ProjectPlaceholder } from './types';

const getSources = ({ sources }: Pick<ProjectData, 'sources'>) =>
  (sources ?? [])
    .filter(Boolean) // antd form.list add undefined to an array whenever we click new item, it will look like [undefined], so we need to filter it.
    .map((eachSource) => eachSource.source);

export const getAllSources = ({
  source,
  ...restFormValues
}: ProjectData): ProjectTemplateOption[] => {
  return [source, ...getSources(restFormValues)].filter(
    Boolean,
  ) as ProjectTemplateOption[];
};

export const getTemplates = async (
  templateIds: number[],
): Promise<ProjectTemplateApi[]> => {
  try {
    return await Promise.all(
      templateIds.map(async (value) =>
        TemplatesAPI.getById<ProjectTemplateApi>(value),
      ),
    );
  } catch (error) {
    logger.error(`Unable to get templates for ${templateIds.join(', ')}`, {
      error,
    });

    return [];
  }
};

export const getFieldFiltersFromTemplates = (
  templates: ProjectTemplateApi[],
): Omit<SimpleSearchFilter, 'match'> => {
  return {
    customFields: templates.reduce((prev, next) => {
      if (next.templateMeta?.fieldFilters?.customFields) {
        prev.push(...next.templateMeta.fieldFilters.customFields);
      }

      return prev;
    }, [] as CustomFieldFilter[]),
    nativeFields: templates.reduce((prev, next) => {
      if (next.templateMeta?.fieldFilters?.nativeFields) {
        prev.push(...next.templateMeta.fieldFilters.nativeFields);
      }

      return prev;
    }, [] as NativeFieldFilter[]),
  };
};
export const getTotalAllocatedMinutesForTeamInTemplate =
  (allocations: TemplateAllocation[]) =>
  (teamId: number): number =>
    allocations
      .filter((x) => x.type !== ResourceType.User && x.teamId === teamId)
      .reduce((total, x) => total + x.minutes * x.duration, 0);

/**
 * Workaround util to get template placeholders
 */
export const getPlaceholdersFromTemplate = ({
  template,
  rolesWithPlaceholder,
  teamsLookup,
}: {
  template: ProjectTemplateApi;
  rolesWithPlaceholder?: Role[];
  teamsLookup: { [id: number]: Team };
}): { [id: number]: Team } => {
  const placeholderList = {};
  const activeRoleIds = rolesWithPlaceholder?.map(({ roleId }) => roleId);
  const {
    templateContent: { projectPhases, allocatedMembers },
    effortPlanning,
  } = template;
  const checkPhases = (phase: ProjectTemplateProjectPhase) => {
    const { assignees } = phase;

    if (assignees == null || assignees.length === 0) {
      return;
    }

    assignees.forEach((assignee) => {
      if (
        assignee.team?.teamId == null ||
        teamsLookup[assignee.team.teamId] == null
      ) {
        return;
      }
      const { teamId } = assignee.team;

      if (!placeholderList[teamId]) {
        placeholderList[teamId] = teamsLookup[teamId];
      }
    });
  };
  const checkTask = (task: ProjectTemplateTask) => {
    const { assignee, subTasks = [] } = task;

    assignee?.teams?.forEach((team) => {
      const { teamId, roleId } = team;

      if (!roleId || (roleId !== -1 && !activeRoleIds?.includes(roleId))) {
        return;
      }

      if (!placeholderList[teamId]) {
        /**
         * Picking the team object from store since team object inside template
         * may be outdated when the default placeholder name got changed
         * due to change in role name.
         */
        placeholderList[teamId] = teamsLookup[teamId] ?? team;
      }
    });
    subTasks.forEach(checkTask);
  };

  projectPhases.forEach((phase) => {
    const { tasks } = phase;

    tasks.forEach(checkTask);
    if (effortPlanning === EffortPlanningConfig.Phase) {
      checkPhases(phase);
    }
  });
  allocatedMembers?.teams.forEach((team) => {
    const { teamId, roleId } = team;

    if (!roleId || (roleId !== -1 && !activeRoleIds?.includes(roleId))) {
      return;
    }

    if (!placeholderList[teamId]) {
      placeholderList[teamId] = teamsLookup[teamId] ?? team;
    }
  });

  return placeholderList;
};

/**
 *
 * @description Returns teams for the template when creating project. Created for fixing bug where new placeholders
 * created when saving project as template didnt show up in task assignee renderer.
 */
export const getPlaceholdersFromTasks = ({
  tasks,
  rolesWithPlaceholder,
  teamsLookup,
}: {
  tasks: ProjectTemplateTask[];
  rolesWithPlaceholder?: Role[];
  teamsLookup: { [id: number]: Team };
}): { [id: number]: Team } => {
  const placeholderList = {};
  const activeRoleIds = rolesWithPlaceholder?.map(({ roleId }) => roleId);

  const checkTask = (task: ProjectTemplateTask) => {
    const { assignee } = task;

    assignee?.teams?.forEach((team) => {
      const { teamId, roleId } = team;

      if (!roleId || (roleId !== -1 && !activeRoleIds?.includes(roleId))) {
        return;
      }

      if (!placeholderList[teamId]) {
        /**
         * Picking the team object from store since team object inside template
         * may be outdated when the default placeholder name got changed
         * due to change in role name.
         */
        placeholderList[teamId] = teamsLookup[teamId] ?? team;
      }
    });
  };

  tasks.forEach(checkTask);

  return placeholderList;
};

export const getPlaceholdersFromTemplates = ({
  templates,
  rolesWithPlaceholder,
  teamsLookup,
}: {
  templates: ProjectTemplateApi[];
  rolesWithPlaceholder?: Role[];
  teamsLookup: { [id: number]: Team };
}): [ProjectPlaceholder[], ProjectPlaceholder[]] => {
  /**
   * list is maintained using name as key
   * since two different placeholders from different templates can have the same name,
   * and they need to be referred as a single placeholder in the form
   * but when making project API call, if the placeholder is assigned to an user,
   * it should be sent as two different teams, for which the duplicates key is used
   * which maintains the unqiue list of placeholders from other templates
   * having the same placeholder name.
   */
  const placeholderList: { [name: string]: ProjectPlaceholder } = {};
  const activeRoleIds = rolesWithPlaceholder?.map(({ roleId }) => roleId);

  const checkTask = (task: ProjectTemplateTask) => {
    const { assignee, subTasks, private: isPrivate } = task;

    assignee?.teams?.forEach((team) => {
      const { roleId, teamType, teamId } = team;

      if (
        teamType !== MemberType.External &&
        (!roleId || !activeRoleIds?.includes(roleId))
      ) {
        return;
      }

      /**
       * Picking the team object from store since team object inside template
       * may be outdated when the default placeholder name got changed
       * due to change in role name.
       */
      const _team = teamsLookup[teamId] ?? team;
      const _current: ProjectPlaceholder = placeholderList[_team.teamName];

      placeholderList[_team.teamName] = !_current?.taskCount
        ? { ..._team, taskCount: 1, privateTaskCount: isPrivate ? 1 : 0 }
        : {
            ..._current,
            taskCount: _current.taskCount + 1,
            privateTaskCount: isPrivate
              ? (_current.privateTaskCount ?? 0) + 1
              : _current.privateTaskCount,
            ...(_current.teamId !== team.teamId && {
              duplicates: { ..._current.duplicates, [team.teamId]: _team },
            }),
          };
    });
    subTasks.forEach(checkTask);
  };

  const checkPhases = (phase: ProjectTemplateProjectPhase) => {
    const { assignees } = phase;

    if (assignees == null || assignees.length === 0) {
      return;
    }

    assignees.forEach((assignee) => {
      if (
        assignee.team?.teamId == null ||
        teamsLookup[assignee.team.teamId] == null
      ) {
        return;
      }

      const _team = teamsLookup[assignee.team.teamId];
      const _current: ProjectPlaceholder = placeholderList[_team.teamName];

      placeholderList[_team.teamName] = !_current?.taskCount
        ? { ..._team, taskCount: 0, privateTaskCount: 0 }
        : {
            ..._current,
            ...(_current.teamId !== _team.teamId
              ? {
                  duplicates: { ..._current.duplicates, [_team.teamId]: _team },
                }
              : {}),
          };
    });
  };

  templates.forEach(
    ({
      templateContent: { projectPhases, allocatedMembers, allocations },
      effortPlanning,
    }) => {
      projectPhases.forEach((phase) => {
        const { tasks } = phase;

        tasks.forEach(checkTask);

        if (effortPlanning === EffortPlanningConfig.Phase) {
          checkPhases(phase);
        }
      });
      const totalAllocatedMinutes = getTotalAllocatedMinutesForTeamInTemplate(
        allocations ?? [],
      );

      allocatedMembers?.teams.forEach((team) => {
        const { teamId: currentTeamId } = team;
        const _team = teamsLookup[currentTeamId] ?? team;

        const exisiting: ProjectPlaceholder = placeholderList[_team.teamName];

        placeholderList[_team.teamName] = !exisiting
          ? {
              ..._team,
              taskCount: 0,
              totalAllocations: totalAllocatedMinutes(currentTeamId),
            }
          : {
              ...exisiting,
              totalAllocations:
                (exisiting.totalAllocations ?? 0) +
                totalAllocatedMinutes(currentTeamId),
              ...(exisiting.teamId !== currentTeamId && {
                duplicates: { ...exisiting.duplicates, [currentTeamId]: _team },
              }),
            };
      });
    },
  );

  const [internalPlaceholders, externalPlaceholders] = Object.values(
    placeholderList,
  ).reduce(
    (prev, _placeholder) => {
      if (_placeholder.teamType === 'EXTERNAL') {
        return [prev[0], [...prev[1], _placeholder]];
      }

      return [[...prev[0], _placeholder], prev[1]];
    },
    [[] as ProjectPlaceholder[], [] as ProjectPlaceholder[]],
  );

  return [internalPlaceholders, externalPlaceholders];
};

export const getTranslationKeyForTeamHint = ({
  taskCount = 0,
  totalAllocations = 0,
}: ProjectPlaceholder): string | null => {
  if (taskCount > 0 && totalAllocations > 0) {
    return 'with_tasks_and_allocated_hours';
  }
  if (totalAllocations > 0) {
    return 'with_allocated_hours_only';
  }
  if (taskCount > 0) {
    return 'with_task_only';
  }

  return null;
};
